import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { Form } from 'semantic-ui-react'
import styles from './Auth.module.css'

import InputWithLabel from '../InputWithLabel/InputWithLabel'
import PrimaryActionButton from "../PrimaryActionButton/PrimaryActionButton"

import { AuthType } from 'types/enums'
import Auth from '@aws-amplify/auth'

const FIRST_STEP = "ENTER_EMAIL"
const SECOND_STEP = "ENTER_CODE"
const SUCCESS_STEP = "SUCCESS_STEP"

export default function Recovery({ type }) {
    const [email, setEmail] = useState("")
    const [currentState, setCurrentState] = useState(FIRST_STEP)

    // States
    // 1. Enter email
    // 2. Enter verification

    async function submitEmail(emailAddress, onError) {
        try {
            setEmail(emailAddress)
            await sendVerificationCode(emailAddress)
            setCurrentState(SECOND_STEP)
        } catch (error) {
            onError()
        }
    }

    async function sendVerificationCode(emailAddress) {
        console.log('send verification code has been called!: ', email)
        try {
            if (email) {
                await Auth.forgotPassword(email);
            } else {
                await Auth.forgotPassword(emailAddress);
            }
        } catch (error) {
            // oh no
        }
    }

    async function submitCodeAndNewPassword(code, password, onError) {
        try {
            let result = await Auth.forgotPasswordSubmit(
                email,
                code,
                password
                );
                console.log('result: ', result)
              setCurrentState(SUCCESS_STEP)
        } catch (error) {
            onError()
            console.log('error submitting new password: ', error)
        }
    }

    if (currentState === FIRST_STEP) {
        return <EmailStep submitEmail={submitEmail} type={type} />
    } else if (currentState === SECOND_STEP) {
        return <VerificationStep sendVerificationCode={sendVerificationCode} submitCodeAndNewPassword={submitCodeAndNewPassword} email={email} type={type} />
    } else if (currentState === SUCCESS_STEP) {
        return <SuccessStep type={type} />
    } else return null
}

function SuccessStep({ type }) {
    let link = ""

    if (type === AuthType.Candidate) {
        link = "/signin"
    } else {
        link = "/employer/signin"
    }
    return (
        <div className={styles.successContainer}>
            <div className={styles.successMessage}>Password Reset Successfully!</div>
            <Link href={link} passHref>
                <div className={styles.signinLink}>Click here to Sign In</div>
            </Link>
        </div>
    )
}

function EmailStep({ submitEmail, type }) {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // handleValidation, then 'submitEmail'

    function handleChange(e) {
        const val = e.target.value;
        setError("");
        setEmail(val);
    }

    function emailIsValid() {
        // todo: improve valiation
        if (email.length > 5) {
            return true; 
        } else {
            setError("Enter a valid email")
            return false;
        }
    }

    function setSubmissionError() {
        setError("Error sending verification code. Try again.")
    }

    async function handleSubmit() {
        if (emailIsValid()) {
            setLoading(true)

            try {
                await submitEmail(email, setSubmissionError)
            } catch (error) {
                console.log('error sending verification code: ', error)
            } finally {
                setTimeout(() => setLoading(false), 1400)
            }   
        }
    }

    function renderHeaderText() {
        return (
            <div className={styles.headerText}>
                {`Recover Your Account`}
            </div>
        )
    }
    
    function renderBottomText() {
        if (type === AuthType.Candidate) {
            return [<Link href="/signin" key="signin">Sign In</Link>, <Link href="/signup" key="signup">Create an Account</Link>]
        } else if (type === AuthType.Enterprise) {
            return [<Link href="/employer/signin" key="signin">Sign In</Link>, <Link href="/employer/signup" key="signup">Create an Account</Link>]            
        }        
    }

    return (
        <div className={styles.container}>
            {renderHeaderText()}
            <Form>
                <div className={styles.component}>
                    <InputWithLabel 
                        placeholder={"Recover your account"} 
                        label="Enter your email address" onChange={handleChange} 
                        value={email} 
                        errorText={error} 
                        showError={error}
                        password={undefined}
                        disabled={loading}
                    />
                    <PrimaryActionButton label="Send Recovery Code" color={undefined} onClick={handleSubmit} loading={loading} />
                </div>
            </Form>
            <div className={styles.bottomText}>
                {renderBottomText()}
            </div>
        </div>
    )
}

function VerificationStep({ submitCodeAndNewPassword, type, email, sendVerificationCode }) {
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")

    function handleChange(e: any) {
        const val = e.target.value;
        setError("");
        setCode(val);
    }

    function handleNewPassword(e: any) {
        const val = e.target.value
        setPassword(val)
    }

    function handleVerifyPassword(e) {
        const val = e.target.value
        setConfirmPassword(val)
    }

    function codeIsValid() {
        if (code.length < 4) {
            return false;
        }
        return true;
    }

    function onSubmissionError() {
        setError("Invalid Recovery Code.")
    }

    function passwordsAreValid() {
        // To-Do: improve this logic 
        return password === confirmPassword;
    }

    function handleSubmit() {
        if (!codeIsValid()) {
            setError("Invalid verification code")
        } else if (!passwordsAreValid()) {
            setError("Make sure both passwords match")
        } else {
            submitCodeAndNewPassword(code, password, onSubmissionError)
        }
    }

    function renderHeaderText() {
        return (
            <div className={styles.headerText}>
                {`Recover Your Account`}
            </div>
        )
    }
    
    function renderBottomText() {
        if (type === AuthType.Candidate) {
            return [<Link href="/signin" key="signin">Sign In</Link>, <Link href="/signup" key="signup">Create an Account</Link>]
        } else if (type === AuthType.Enterprise) {
            return [<Link href="/employer/signin" key="signin">Sign In</Link>, <Link href="/employer/signup" key="signup">Create an Account</Link>]            
        }        
    }

    const ResendText = () => {
        return (
            <div className={styles.recoverySent}>{`A recovery code was sent to `}<div className={styles.emailText}>{email}</div>&nbsp;&nbsp;<div className={styles.resend} onClick={sendVerificationCode}>Resend Code</div></div>
        )
    }

    return (
        <div className={styles.container}>
            {renderHeaderText()}
            <Form>
                <div className={styles.component}>
                    <ResendText />
                    <InputWithLabel 
                        placeholder={"Recover your account"} 
                        label="Verification Code" onChange={handleChange} 
                        value={code} 
                        errorText={error} 
                        showError={error}
                        password={undefined}
                        disabled={loading}
                        labelStyle={{ fontSize: "12px", fontWeight: "normal" }}
                    />
                    <InputWithLabel 
                        placeholder={"Enter your new password"} 
                        label="Enter a new password" onChange={handleNewPassword} 
                        value={password} 
                        password={true}
                        disabled={loading}
                        labelStyle={{ fontSize: "12px", fontWeight: "normal" }}
                    />
                    <InputWithLabel 
                        placeholder={"Verify your new password"} 
                        label="Confirm new password" onChange={handleVerifyPassword} 
                        value={confirmPassword} 
                        password={true}
                        disabled={loading}
                        labelStyle={{ fontSize: "12px", fontWeight: "normal" }}
                    />                                
                    <PrimaryActionButton label="Submit Recovery Code" color={undefined} onClick={handleSubmit} loading={loading} />
                </div>
            </Form>
            <div className={styles.bottomText}>
                {renderBottomText()}
            </div>
        </div>
    )
}


// map out the flow
// 1. Form to enter email address to recover the password
// 2. Once submitted recovery request, change component state to 'pending verification code'
// 3. Submit the verification code, if successful -> log the user in and have them change their password ; redirect to 'candidate/changepassword' or 'employer/changepassword' screens 
// 4. The "Change Password" screen will be the same for both the employer and the candidate 